import mixpanel from 'mixpanel-browser';

let initialized = false;

// Initialize Mixpanel
// Replace 'YOUR_MIXPANEL_TOKEN' with your actual Mixpanel project token
// You can get this from your Mixpanel project settings
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || 'fadb8f2b0b7569b959cdba62a0173a5e';

// Initialize Mixpanel
export const initMixpanel = () => {
  if (typeof window === 'undefined' || initialized) return;

  if (!MIXPANEL_TOKEN) {
    return;
  }

  try {
    const isProduction = process.env.NODE_ENV === 'production';

    // Session Replay configuration
    // According to official Mixpanel docs: https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay
    const sessionReplayEnabled = true; // Always enabled

    // record_sessions_percent: number between 0-100 (docs show 0-100, not 0-1)
    const recordSessionsPercent = 100; // Record 100% of sessions

    // FIX: Add persistence fallback for mobile apps (iOS/Android)
    // Try localStorage first, then sessionStorage, then memory
    let persistenceType = 'localStorage';
    try {
      const test = '__mixpanel_test__';
      localStorage.setItem(test, '1');
      localStorage.removeItem(test);
    } catch (e) {
      try {
        const test = '__mixpanel_test__';
        sessionStorage.setItem(test, '1');
        sessionStorage.removeItem(test);
        persistenceType = 'sessionStorage';
      } catch (e2) {
        persistenceType = 'memory';
      }
    }

    const initConfig: any = {
      debug: !isProduction, // show console logs only in dev
      track_pageview: true,
      persistence: persistenceType, // Use fallback persistence
      ignore_dnt: !isProduction,
      batch_requests: isProduction,
      autotrack: true,
      loaded: (mixpanelInstance: any) => {
        // Additional setup after Mixpanel loads
        // Identify user (you can customize this based on your needs)
        // For a portfolio site, we might use a visitor ID or keep it anonymous
        const visitorId = localStorage.getItem('visitor_id') || `visitor_${Date.now()}`;
        localStorage.setItem('visitor_id', visitorId);
        mixpanelInstance.identify(visitorId);
        
        // Set user properties
        mixpanelInstance.people.set({
          '$name': 'Portfolio Visitor',
          'first_visit': !localStorage.getItem('has_visited'),
          'visit_count': parseInt(localStorage.getItem('visit_count') || '0') + 1
        });
        
        if (!localStorage.getItem('has_visited')) {
          localStorage.setItem('has_visited', 'true');
        }
        localStorage.setItem('visit_count', (parseInt(localStorage.getItem('visit_count') || '0') + 1).toString());
      }
    };

    // Add Session Replay configuration if enabled
    // Official format: record_sessions_percent (0-100)
    if (sessionReplayEnabled && recordSessionsPercent > 0) {
      initConfig.record_sessions_percent = Math.max(0, Math.min(100, recordSessionsPercent));

      // This combination forces Mixpanel to stop masking text
      initConfig.record_mask_text_selector = '';
      initConfig.record_unmask_text_selector = '*';

      // This ensures images and media aren't blocked (grey boxes)
      initConfig.record_block_selector = '';

      // Optional: ensures it doesn't mask common inputs automatically
      initConfig.record_mask_all_inputs = false;

      initConfig.record_unmask_selector = '*';
    }

    mixpanel.init(MIXPANEL_TOKEN, initConfig);

    mixpanel.set_config({ test: false });
    initialized = true;
  } catch (error) {
    // Silently fail - don't break the app
  }
};

// Track custom events
// FIX: Auto-initialize if not initialized yet
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window === 'undefined') return;

  // FIX: Attempt to initialize if not already initialized
  if (!initialized) {
    try {
      initMixpanel();
    } catch (e: any) {
      // Silently fail
    }
  }

  if (!initialized) {
    return;
  }

  const combinedProps = {
    ...properties,
    timestamp: new Date().toISOString(),
  };

  try {
    mixpanel.track(eventName, combinedProps);
  } catch (error) {
    // Silently fail - don't break the app
  }
};

// Track page views
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  trackEvent('Page View', {
    page_name: pageName,
    ...properties,
  });
};

// Track section views (for portfolio sections)
export const trackSectionView = (sectionName: string) => {
  trackEvent('Section Viewed', {
    section_name: sectionName,
  });
};

// Track navigation clicks
export const trackNavigationClick = (navItem: string) => {
  trackEvent('Navigation Clicked', {
    nav_item: navItem,
  });
};

// Track theme toggle
export const trackThemeToggle = (newTheme: 'light' | 'dark') => {
  trackEvent('Theme Toggled', {
    theme: newTheme,
  });
};

// Track project interactions
export const trackProjectView = (projectTitle: string, projectCategory: string) => {
  trackEvent('Project Viewed', {
    project_title: projectTitle,
    project_category: projectCategory,
  });
};

export const trackProjectClick = (projectTitle: string, projectCategory: string, link: string) => {
  trackEvent('Project Clicked', {
    project_title: projectTitle,
    project_category: projectCategory,
    project_link: link,
  });
};

export const trackProjectTabFilter = (filter: string) => {
  trackEvent('Project Tab Filtered', {
    filter: filter,
  });
};

// Track contact form interactions
export const trackContactFormStart = () => {
  trackEvent('Contact Form Started', {});
};

export const trackContactFormSubmit = (success: boolean, errorMessage?: string) => {
  trackEvent('Contact Form Submitted', {
    success: success,
    error_message: errorMessage,
  });
};

// Track social media clicks
export const trackSocialClick = (platform: string, url: string) => {
  trackEvent('Social Link Clicked', {
    platform: platform,
    url: url,
  });
};

// Track scroll depth (optional)
export const trackScrollDepth = (depth: number) => {
  trackEvent('Scroll Depth', {
    depth_percentage: depth,
  });
};

export default mixpanel;

